import React from 'react'
import {
  Box,
  Card,
  CardContent,
  Typography,
  useTheme,
  Skeleton
} from '@mui/material'
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend
} from 'recharts'
import { formatCurrency } from '../../utils/formatters'

const SalesChart = ({ data, loading, title = "Evolución de Facturas por Estado" }) => {
  const theme = useTheme()
  

  const formatTooltip = (value, name) => {
    if (name === 'totalAmount') {
      return [formatCurrency(value), 'Total']
    }
    if (name === 'paidAmount') {
      return [formatCurrency(value), 'Pagadas']
    }
    if (name === 'pendingAmount') {
      return [formatCurrency(value), 'Por Cobrar']
    }
    if (name === 'overdueAmount') {
      return [formatCurrency(value), 'Vencidas']
    }
    return [formatCurrency(value), name]
  }

  const formatXAxisLabel = (tickItem) => {
    const date = new Date(tickItem)
    return date.toLocaleDateString('es-CL', { 
      month: 'short', 
      year: '2-digit' 
    })
  }

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {title}
          </Typography>
          <Skeleton variant="rectangular" height={300} />
        </CardContent>
      </Card>
    )
  }

  if (!data || data.length === 0) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {title}
          </Typography>
          <Box 
            sx={{ 
              height: 300, 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center' 
            }}
          >
            <Typography variant="body2" color="text.secondary">
              No hay datos disponibles
            </Typography>
          </Box>
        </CardContent>
      </Card>
    )
  }


  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        <Typography variant="h6" gutterBottom>
          {title}
        </Typography>
        
        
        <Box sx={{ width: '100%', flexGrow: 1, mt: 2, minHeight: 300 }}>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={data} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid 
                strokeDasharray="3 3" 
                stroke={theme.palette.divider}
              />
              <XAxis 
                dataKey="monthDate" 
                tickFormatter={formatXAxisLabel}
                stroke={theme.palette.text.secondary}
                fontSize={12}
              />
              <YAxis 
                tickFormatter={(value) => formatCurrency(value).replace('$', '$')}
                stroke={theme.palette.text.secondary}
                fontSize={12}
              />
              <Tooltip 
                formatter={formatTooltip}
                labelFormatter={(label) => {
                  const date = new Date(label);
                  return `Mes: ${date.toLocaleDateString('es-CL', { 
                    month: 'long', 
                    year: 'numeric' 
                  })}`;
                }}
                contentStyle={{
                  backgroundColor: theme.palette.background.paper,
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: theme.shape.borderRadius
                }}
              />
              <Legend />
              <Line 
                type="monotone" 
                dataKey="totalAmount" 
                stroke={theme.palette.primary.main}
                strokeWidth={4}
                dot={{ 
                  fill: theme.palette.primary.main, 
                  strokeWidth: 3, 
                  r: 6,
                  stroke: '#ffffff'
                }}
                activeDot={{ 
                  r: 8, 
                  stroke: theme.palette.primary.main,
                  strokeWidth: 3,
                  fill: '#ffffff'
                }}
                name="Total"
                filter="drop-shadow(0 2px 4px rgba(0,0,0,0.1))"
              />
              <Line 
                type="monotone" 
                dataKey="paidAmount" 
                stroke={theme.palette.success.main}
                strokeWidth={3}
                dot={{ 
                  fill: theme.palette.success.main, 
                  strokeWidth: 2, 
                  r: 4,
                  stroke: '#ffffff'
                }}
                activeDot={{ r: 6 }}
                name="Pagadas"
                strokeDasharray="5 5"
              />
              <Line 
                type="monotone" 
                dataKey="pendingAmount" 
                stroke={theme.palette.warning.main}
                strokeWidth={3}
                dot={{ 
                  fill: theme.palette.warning.main, 
                  strokeWidth: 2, 
                  r: 4,
                  stroke: '#ffffff'
                }}
                activeDot={{ r: 6 }}
                name="Por Cobrar"
                strokeDasharray="3 3"
              />
              <Line 
                type="monotone" 
                dataKey="overdueAmount" 
                stroke={theme.palette.error.main}
                strokeWidth={3}
                dot={{ 
                  fill: theme.palette.error.main, 
                  strokeWidth: 2, 
                  r: 4,
                  stroke: '#ffffff'
                }}
                activeDot={{ r: 6 }}
                name="Vencidas"
                strokeDasharray="8 8"
              />
            </LineChart>
          </ResponsiveContainer>
        </Box>
      </CardContent>
    </Card>
  )
}

export default SalesChart
